import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Wave 2 — Card variants aligned to design tokens.
// default     → standard list item / container (no hover)
// elevated    → hero / summary card with soft shadow
// interactive → clickable card (hover lifts background + border)
// ghost       → no bg / no border; use for nesting inside other cards
const cardVariants = cva(
  "rounded-xl border text-foreground transition-all duration-normal ease-smooth",
  {
    variants: {
      variant: {
        default:     "bg-surface-card border-subtle",
        elevated:    "bg-surface-card border-subtle shadow-md hover:shadow-lg",
        interactive: "bg-surface-card border-subtle hover:bg-surface-card-hover hover:border-strong cursor-pointer",
        ghost:       "bg-transparent border-transparent",
      },
    },
    defaultVariants: { variant: "default" },
  }
)

const Card = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("font-semibold leading-none tracking-tight", className)} {...props} />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm text-foreground-muted", className)} {...props} />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants }
